import { MouseEvent, useCallback, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import {
  BuyingSignalCard,
  BuyingSignalsSkeleton,
  ScrollableLeadList,
} from "components/document_details/BuyingSignalCard";
import PageBadge from "components/document_details/PageBadge";
import { CreateLeadFormOutput } from "components/leads/create_lead/utils";
import {
  SaveLeadDrawerProvider,
  useSaveLeadDrawer,
} from "components/leads/saving/SaveLeadDrawerProvider";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import { appRoutes } from "lib/appRoutes";
import { SourceLocation } from "lib/core_components/fileViewerAnnotations";
import { useCitationNavigator } from "lib/core_components/useCitationNavigator";
import {
  documentFindLeadsCachedQueryKey,
  DocumentFindLeadsLead,
  useDocumentFindLeadsCached,
} from "lib/hooks/api/documents/useDocumentFindLeadsCached";
import {
  DOCUMENT_FIND_LEADS_TERM_MAX_LENGTH,
  useDocumentFindLeadsForTerm,
} from "lib/hooks/api/documents/useDocumentFindLeadsForTerm";
import { useDocumentFindLeadsRerun } from "lib/hooks/api/documents/useDocumentFindLeadsRerun";
import { useDocumentFindLeadsStart } from "lib/hooks/api/documents/useDocumentFindLeadsStart";
import { useDocumentFindLeadsUpdates } from "lib/hooks/api/documents/useDocumentFindLeadsUpdates";
import {
  DocumentFindLeadsMetadata,
  useCreateLeadOneClick,
} from "lib/hooks/api/leads/useCreateLeadOneClick";
import { useAssertCurrentUser, useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useURLState } from "lib/hooks/useURLState";
import { LeadEntityType } from "lib/types/graphQLEnums";

type DocumentFindLeadsProps = {
  documentId: string;
  hasDocumentLeads: boolean;
  refetchDocument: () => void;
};

const createLeadMutationKey = (documentId: string) => ["createLead", documentId];

export default function DocumentFindLeads(props: DocumentFindLeadsProps) {
  return (
    <SaveLeadDrawerProvider trackingData={{ "Context source": "Document Find Leads" }}>
      <DocumentFindLeadsContent {...props} />
    </SaveLeadDrawerProvider>
  );
}

function DocumentFindLeadsContent({
  documentId,
  hasDocumentLeads,
  refetchDocument,
}: DocumentFindLeadsProps) {
  const navigateToCitation = useCitationNavigator();
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();

  const {
    data: cached,
    isLoading: isCachedLoading,
    isError: isCachedError,
  } = useDocumentFindLeadsCached(documentId);

  // Tracks whether we've already fired the auto-trigger for the current documentId.
  // Prevents re-triggering if the cache query re-renders with "missing" after a failure.
  const hasAutoTriggered = useRef(false);

  // Reset the auto-trigger guard whenever we switch documents.
  useEffect(() => {
    hasAutoTriggered.current = false;
  }, [documentId]);

  // Each mutation writes `running` into the cached query, which turns the subscription on.
  const { mutate: startFindLeads, isLoading: isStarting } = useDocumentFindLeadsStart();
  const { mutate: rerunFindLeads, isLoading: isRerunning } = useDocumentFindLeadsRerun();

  // Auto-trigger on tab mount when there's no cached result and no work in flight.
  // The ref guard ensures we fire at most once per document, preventing a re-trigger
  // if the cache query re-renders with "missing" after a failure.
  useEffect(() => {
    if (isCachedLoading || isCachedError) return;
    if (!cached) return;
    if (cached.status !== "missing") return;
    if (hasAutoTriggered.current) return;
    hasAutoTriggered.current = true;
    startFindLeads({ documentId });
  }, [cached, documentId, isCachedError, isCachedLoading, startFindLeads]);

  // The subscription invalidates the cached query on completed/failed, so cached.status
  // is the single source of truth for the workflow's lifecycle.
  const operationId = cached?.operationId ?? null;
  useDocumentFindLeadsUpdates({
    documentId,
    operationId,
    enabled: cached?.status === "running",
  });

  const navigateToChunk = useCallback(
    (lead: DocumentFindLeadsLead) => {
      // Clicks made before the viewer finishes loading are deferred and replayed
      // once it's ready, so the first badge click after opening is never dropped.
      const locations: SourceLocation[] = lead.locations?.map((loc) => ({
        pageNumber: loc.pageNumber,
        bbox: loc.bbox ?? null,
      })) ?? [{ pageNumber: lead.pageNumbers[0], bbox: null }];
      navigateToCitation(locations);
    },
    [navigateToCitation],
  );

  const handleRerun = () => {
    rerunFindLeads({ documentId });
  };

  // Refetch find-leads result + document metadata after a lead has been created/linked,
  // so cards flip to View Lead on the first save.
  const refreshAfterLeadSaved = useCallback(() => {
    void queryClient.invalidateQueries(documentFindLeadsCachedQueryKey(documentId));
    refetchDocument();
  }, [documentId, queryClient, refetchDocument]);

  const isAdmin = currentUser?.isAdmin === true;

  // Admin-only: with `?signalTerm=` in the URL, Re-run matches the document against that term
  // instead of the team profile. Never shown on the page, so the result screenshots like any other.
  const [linkedTerm] = useURLState("signalTerm", "", z.string());
  const signalTerm = linkedTerm.trim().slice(0, DOCUMENT_FIND_LEADS_TERM_MAX_LENGTH);
  const {
    mutate: findLeadsForTerm,
    data: termData,
    variables: termVariables,
    isLoading: isFindingForTerm,
    isError: isTermError,
  } = useDocumentFindLeadsForTerm();
  // Scoped to the document it ran for, so switching documents in the drawer drops a stale run.
  const isTermMode =
    termVariables?.documentId === documentId && (isFindingForTerm || !!termData || isTermError);

  const handleRerunClick = () => {
    if (signalTerm) {
      findLeadsForTerm({ documentId, term: signalTerm });
    } else {
      handleRerun();
    }
  };

  const result = isTermMode ? termData?.documentFindLeadsForTerm ?? null : cached?.result ?? null;
  const leads = result?.leads ?? [];
  const showProgress = isTermMode
    ? isFindingForTerm
    : cached?.status === "running" || isStarting || isRerunning;
  const showFailure = isTermMode ? isTermError : cached?.status === "failed";

  return (
    // Term and team-profile runs look identical, so this lets screenshot tooling tell them apart.
    <Container column gap={12} data-signals-source={isTermMode ? "term" : "team"}>
      <Flex column gap={4}>
        <Text h3 bold>
          {result && !showProgress && !showFailure
            ? `${leads.length} Buying signals found in document`
            : "Buying signals"}
        </Text>
        <Text description color="sysTextSecondary">
          Matching your team profile
        </Text>
      </Flex>

      {showProgress && <BuyingSignalsSkeleton />}

      {showFailure && (
        <EmptyState
          width="100%"
          type="loadError"
          heading="Empty, But Full of Potential!"
          description="Signals couldn't be generated for this document. Please try again later."
        />
      )}

      {isAdmin && !showProgress && (
        <Flex>
          <Button buttonType="secondary" onClick={handleRerunClick} loading={isRerunning}>
            Re-run
          </Button>
        </Flex>
      )}

      {result && !showProgress && leads.length === 0 && (
        <EmptyState
          width="100%"
          type="noResults"
          heading="No relevant signals in this document"
          description="Nothing in this document matched the priorities in your team profile. The document may still be worth a skim for general context."
        />
      )}

      {result && leads.length > 0 && !showProgress && (
        <ScrollableLeadList>
          <Flex column gap={12}>
            {leads.map((lead, index) => (
              <LeadCard
                key={`${index}-${lead.leadTitle}`}
                lead={lead}
                documentId={result.documentId ?? documentId}
                documentName={result.documentName ?? ""}
                buyerId={result.buyerId ?? ""}
                hasDocumentLeads={hasDocumentLeads}
                canCreateLead={!isTermMode}
                onLeadSaved={refreshAfterLeadSaved}
                onNavigateToChunk={navigateToChunk}
              />
            ))}
          </Flex>
        </ScrollableLeadList>
      )}
    </Container>
  );
}

type LeadCardProps = {
  lead: DocumentFindLeadsLead;
  documentId: string;
  documentName: string;
  buyerId: string;
  hasDocumentLeads: boolean;
  // False for term runs: the button still shows so screenshots match a normal result, but does
  // nothing, since an admin may be switched into a customer account and saving would create the
  // lead in that customer's team.
  canCreateLead: boolean;
  onLeadSaved: () => void;
  onNavigateToChunk: (lead: DocumentFindLeadsLead) => void;
};

function LeadCard({
  lead,
  documentId,
  documentName,
  buyerId,
  hasDocumentLeads,
  canCreateLead,
  onLeadSaved,
  onNavigateToChunk,
}: LeadCardProps) {
  const currentUser = useAssertCurrentUser();
  const entities = [
    { entityId: documentId, entityType: LeadEntityType.Document },
    ...(buyerId ? [{ entityId: buyerId, entityType: LeadEntityType.Buyer }] : []),
  ];

  const drawerDefaultValues: Partial<CreateLeadFormOutput> = {
    name: lead.leadTitle,
    entities,
    assignedToId: currentUser.guid,
  };

  const findLeadsMetadata: DocumentFindLeadsMetadata = {
    source: "document_find_leads",
    leadType: lead.leadType,
    motivation: lead.leadMotivation ?? undefined,
    matchingText: lead.matchingText ?? undefined,
    pageNumbers: lead.pageNumbers,
    locations: lead.locations?.map((loc) => ({
      pageNumber: loc.pageNumber,
      bbox: loc.bbox ?? undefined,
    })),
  };

  const { createLeadFromDefaultValues, isLoading: isCreatingLead } = useCreateLeadOneClick({
    trackingData: {
      "Context source": "Document Find Leads",
      "Data type": "DOCUMENT",
      "Document ID": documentId,
      "Document name": documentName,
      "Lead type": lead.leadType,
    },
    defaultValues: {
      name: lead.leadTitle,
      entities,
      assignedToId: currentUser.guid,
    },
    metadata: findLeadsMetadata,
    mutationKey: createLeadMutationKey(documentId),
    onCreateLead: onLeadSaved,
  });

  const { openLeadDrawer } = useSaveLeadDrawer();

  // Any in-flight createLead for this document means a sibling card is already racing
  // the cache flip; route this click through the drawer instead of starting a parallel
  // fast-create. Settles automatically when the mutation finishes — no manual reset.
  const isAnyCreatePending = useIsMutating({ mutationKey: createLeadMutationKey(documentId) }) > 0;

  const handleCreateLead = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!canCreateLead) return;

    if (hasDocumentLeads || isAnyCreatePending) {
      openLeadDrawer({
        entityType: "document",
        entityId: documentId,
        defaultValues: drawerDefaultValues,
        buyerMatchOnly: true,
        findLeadsMetadata,
        onLeadSaved: onLeadSaved,
      });
      return;
    }

    createLeadFromDefaultValues(e);
  };

  const pageNumber = lead.pageNumbers[0];
  const isViewLead = !!lead.createdLeadId;

  return (
    <BuyingSignalCard column gap={12}>
      <Text bold>{lead.leadTitle}</Text>
      {lead.leadMotivation && (
        <Text>
          {lead.leadMotivation}
          {pageNumber !== undefined && (
            <>
              {" "}
              <PageBadge type="button" onClick={() => onNavigateToChunk(lead)}>
                [p{pageNumber}]
              </PageBadge>
            </>
          )}
        </Text>
      )}
      <Flex gap={8}>
        {isViewLead ? (
          <Button buttonType="secondary" as="a" {...appRoutes.leadDetail(lead.createdLeadId ?? "")}>
            View Lead
          </Button>
        ) : (
          <Button buttonType="primary" onClick={handleCreateLead} loading={isCreatingLead}>
            Create Lead
          </Button>
        )}
      </Flex>
    </BuyingSignalCard>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.sysBackgroundDefault,
  padding: 24,
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
}));
